import React, {useCallback, useMemo} from 'react';
import type {AstView, EnrichedAstNode} from 'rendition-core';

import TabbedButtonToggle from 'Components/Tailwind/TabbedButtonToggle';
import {useMultiNodeValue} from 'Hooks/useMultiNodeValue';
import {Switch} from 'Components/Tailwind/Switch';
import {EditorSectionTitle} from './EditingTitle';
import {Separator, segmentedControlClassNames} from 'Views/Preview/Builder/EditControls';

interface Props {
    view: AstView;
    setView: (view: AstView) => void;
    selectedNodes: EnrichedAstNode[];
}

const options: Array<{value: Exclude<React.CSSProperties['objectFit'], undefined>; icon: React.ReactNode}> = [
    {value: 'fill', icon: 'Fill'},
    {value: 'contain', icon: 'Contain'},
    {value: 'cover', icon: 'Cover'},
];

const bgOptions: Array<{
    value: Exclude<React.CSSProperties['backgroundSize'], undefined | number>;
    icon: React.ReactNode;
}> = [
    {value: 'contain', icon: 'Contain'},
    {value: 'cover', icon: 'Cover'},
];

export const ObjectFitToggle: React.FC<React.PropsWithChildren<Props>> = ({view, setView, selectedNodes}) => {
    const allImageTypes = useMemo(
        () => selectedNodes.every((n) => n.styles.backgroundImage || n.type === 'Image'),
        [selectedNodes]
    );
    const allImages = useMemo(() => selectedNodes.every((n) => n.type === 'Image'), [selectedNodes]);

    const accessFn = useCallback((n) => (n.styles.backgroundImage ? n.styles.backgroundSize : n.styles.objectFit), []);

    const updateObjectFit = useCallback((node: EnrichedAstNode, fit: React.CSSProperties['objectFit']) => {
        node.styles.objectFit = fit;
    }, []);

    const updateBgSize = useCallback((node: EnrichedAstNode, bgSize: React.CSSProperties['backgroundSize']) => {
        node.styles.backgroundSize = bgSize;
    }, []);

    const assignFn = useCallback(
        (n, value) => {
            if (n.styles.backgroundImage) {
                updateBgSize(n, value);
            } else {
                updateObjectFit(n, value);
            }
        },
        [updateBgSize, updateObjectFit]
    );

    const {value, updateNodes} = useMultiNodeValue(selectedNodes, accessFn, assignFn);

    const getPreserveAspectRatio = useCallback(
        (n: EnrichedAstNode) => n.calculatedLayoutAbstraction.preserveAspectRatio ?? true,
        []
    );
    const setPreserveAspectRatio = useCallback((n: EnrichedAstNode, v: boolean) => {
        n.inputLayoutAbstraction.preserveAspectRatio = v;
    }, []);

    const {value: preserveAspectRatio, updateNodes: updatePreserveAspectRatio} = useMultiNodeValue(
        selectedNodes,
        getPreserveAspectRatio,
        setPreserveAspectRatio
    );

    if (!allImageTypes) return null;

    return (
        <div className="flex w-full flex-col items-start justify-between gap-3">
            <div className="flex w-full items-center justify-between">
                <EditorSectionTitle title="Image" />

                <TabbedButtonToggle
                    classNames={segmentedControlClassNames}
                    value={value}
                    items={allImages ? options : bgOptions}
                    onClick={updateNodes}
                />
            </div>
            {allImageTypes && (
                <div className="flex flex-row-reverse items-center gap-2">
                    <h4 className="text-[10px] font-extrabold capitalize text-white">Preserve Aspect Ratio</h4>
                    <Switch
                        checked={preserveAspectRatio ?? true}
                        onChange={updatePreserveAspectRatio}
                        size="xs"
                        description="Preserve Aspect Ratio"
                    />
                </div>
            )}
            <Separator />
        </div>
    );
};
